/**
 * `<MemberPersistentPanel/>` — member-side persistent panel (#194 PR 1).
 *
 * Mounts on `/me*` — the member-facing route family
 * (`/me/audit`, `/me/insights`, `/me/comply`, `/me/forecast`,
 * `/me/hours`, `/me/workspaces`, etc.). Decision lives in
 * `lib/persistentPanel.ts`.
 *
 * **Sections (running list — extended per IA-rework PR):**
 *   1. ~~`<ThreeCirclesPrivacyStrip audience="member">`~~ (PR 1) —
 *      **REMOVED** in #208. The strip's three-cards-side-by-side
 *      layout compressed to single-word columns inside the 330px
 *      panel; the page-level `/me/audit` interstitial + dismissed
 *      callout already carry the trust framing with room to render.
 *      Founder direction 2026-05-10 after spot-check screenshot.
 *   2. ~~`<ReciprocalTrustNote audience="member" surface="audit">`~~
 *      (PR 1) — **REMOVED** in #208. The page header on `/me/audit`
 *      already says "The same activity log is visible to your team
 *      lead" — the panel duplicate was triple-redundant copy.
 *   3. `<HoursPillSection>` (#212, sub-issue of #155) — hours-vs-
 *      contract pill with a deep link to `/me/hours`. Closes the
 *      wireframe-alignment gap identified by the #210 audit; the
 *      load-bearing BP-2 worker-friendly signal letting the worker
 *      see their cycle delta ambiently. Self-hides on no-membership
 *      / no-data states so empty teams don't carry an empty pill.
 *   4. `<WhatNeedsYouSection>` (PR 4, #200) — actionable summary
 *      composed from `useWorkspaces()` + `useMySchedule()`. Surfaces
 *      open-cycle counts per team and upcoming shifts in the next 7
 *      days, each with a one-click deep link. Closes T3's click-depth
 *      complaint. Always-on per #193.
 *   5. `<MeAuditTickerSection>` (PR 2, #196) — 3-row own-audit ticker
 *      with a deep link to `/me/audit`. Always-on per #193 — the
 *      member's own audit surface is a worker-trust pillar, not gated
 *      on a registry component.
 *
 * (#1294 removed the former foot-of-panel `<PlanFootPill>` along with
 * the Billing surface it deep-linked to.)
 *
 * **Section ordering rationale.** Hours pill sits at the top because
 * it's the load-bearing trust signal per the wireframe spec — the
 * worker's first read on the panel should be "where do I stand vs my
 * contract right now?". What-needs-you sits ABOVE the audit ticker
 * because actionable items beat retrospective context for the
 * member's daily workflow — a member opening `/me/*` should see "what
 * to do next" before "what just happened."
 *
 * **Brand-pillar discipline.** Calm by default; member-facing copy
 * never frames the platform as monitoring/surveillance — only as
 * boundary protection ("yours alone", "your team lead", "your team").
 */

import { ForecastPillSectionMember } from "./ForecastPillSection";
import { HoursPillSection } from "./HoursPillSection";
import { MeAuditTickerSection } from "./MeAuditTickerSection";
import { WhatNeedsYouSection } from "./WhatNeedsYouSection";

export function MemberPersistentPanel() {
  return (
    <aside
      aria-label="Your context panel"
      data-testid="member-persistent-panel"
      data-variant="member"
      className="flex flex-col gap-4 p-3"
    >
      {/* #212 (sub-issue of #155) — hours-vs-contract pill. Self-hides
          on no-membership / no-data so it doesn't render an empty box
          for fresh members. The wireframe-canonical first row of the
          member panel per `docs/research/ui-wireframes-page-by-page.md`
          lines 499-514. #193 wrap: gated on `hours_balance_member`
          inside the section (the team_id resolves from the hours
          fetch's `scope.team_id`, so the `<ComponentGate>` sits below
          the fetch rather than wrapping the section here). */}
      <HoursPillSection />
      {/* PR 4 (#200) — actionable summary. Always-on per #193 (core
          member affordance — the worker-trust pillar doesn't gate
          "what you need to do today" behind a registry component). */}
      <WhatNeedsYouSection />
      {/* #162 — Forecast pill (member variant). The section self-gates
          on the `forecast` component (resolved from the sustainability
          fetch's `scope.team_id`); section self-hides on
          `state="no_data"` so empty teams don't carry an empty pill. */}
      <ForecastPillSectionMember />
      {/* PR 2 (#196) — own-audit ticker. Always-on per #193 — the
          member's own audit feed is a worker-trust pillar, not gated. */}
      <MeAuditTickerSection />
    </aside>
  );
}
